import { useState, useContext, useEffect, useMemo } from 'react';
import { Check, Minus, Plus, X } from 'lucide-react';
import { formatCurrency } from '../../Utils';
import CartContext from "../../Context/Cart/CartContext";
import { toast } from 'react-hot-toast';
import { useBrand } from '../../Context/Brand/BrandContext';

export default function ItemCustomization({ item, onClose, onSave, initialSelections, initialInstructions, initialQuantity }) {
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#1A7F6D';
  const [quantity, setQuantity] = useState(initialQuantity || 1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState(initialInstructions || '');
  const { addToCart } = useContext(CartContext);
  const isEditing = !!onSave;

  const optionGroups = useMemo(() => (
    item.menu_item_option_groups
      ?.map((join) => join.menu_option_groups)
      .filter(Boolean) || []
  ), [item]);

  useEffect(() => {
    if (initialSelections) {
      setSelectedOptions(initialSelections);
      return;
    }

    const defaults = {};
    optionGroups.forEach((group) => {
      const defaultOption = group.menu_options?.find((option) => option.is_default);
      if (defaultOption) defaults[group.id] = defaultOption.id;
    });
    setSelectedOptions(defaults);
  }, [initialSelections, optionGroups]);

  const itemName = item.item_name || item.name || 'Menu item';
  const itemImage = item.image || item.image_url || '';
  const itemDescription = item.description || '';
  const basePrice = Number(item.price || 0);

  const toggleOption = (groupId, optionId) => {
    setSelectedOptions((previous) => ({
      ...previous,
      [groupId]: previous[groupId] === optionId ? null : optionId,
    }));
  };

  const getSelectedAddOns = () => {
    const extras = [];
    optionGroups.forEach((group) => {
      const selectedId = selectedOptions[group.id];
      if (!selectedId) return;
      const option = group.menu_options?.find((entry) => entry.id === selectedId);
      if (option && Number(option.price_modifier || 0) > 0) extras.push(option);
    });
    return extras;
  };

  const calculateUnitTotal = () => {
    const extras = getSelectedAddOns().reduce((sum, option) => sum + Number(option.price_modifier || 0), 0);
    return basePrice + extras;
  };
  const unitTotal = calculateUnitTotal();
  const finalTotal = unitTotal * quantity;

  const handleAddToCart = () => {
    const selectedOpts = optionGroups.map((group) => ({
      groupId: group.id,
      groupName: group.name,
      optionId: selectedOptions[group.id],
      optionName: group.menu_options?.find((option) => option.id === selectedOptions[group.id])?.name || null,
    })).filter((option) => option.optionId);

    const cartItem = {
      ...item,
      item_public_id: Math.random().toString(36).substr(2, 9),
      quantity,
      selectedOptions: selectedOpts,
      specialInstructions,
      price: calculateUnitTotal(),
    };

    if (isEditing) {
      onSave(cartItem);
      toast.success('Cart updated');
    } else {
      addToCart(cartItem);
      toast.success('Added to cart');
    }
    onClose();
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white text-neutral-950">
      <header className="relative h-[210px] shrink-0 overflow-hidden bg-neutral-100 sm:h-[240px]">
        {itemImage ? (
          <img src={itemImage} alt={itemName} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 text-neutral-300">
            <span className="text-[13px] font-semibold">No image</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/5 to-black/25" />
        <div className="relative z-10 flex items-center justify-between gap-4 px-4 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 shadow-sm backdrop-blur"
            aria-label="Close item details"
          >
            <X className="h-4 w-4 text-neutral-700" />
          </button>
          <div className="rounded-full bg-white/95 px-3 py-2 text-[13px] font-bold text-neutral-900 shadow-sm backdrop-blur">
            {formatCurrency(unitTotal)}
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto bg-white px-5 pb-6">
        <div className="mx-auto max-w-2xl">
          <section className="border-b border-neutral-100 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-[24px] font-bold leading-tight tracking-tight text-neutral-950">{itemName}</h1>
              </div>
              <div className="shrink-0 text-[15px] font-bold text-neutral-950">
                {formatCurrency(basePrice)}
              </div>
            </div>
            {itemDescription && (
              <p className="mt-3 w-full text-[14px] leading-6 text-neutral-600">{itemDescription}</p>
            )}

            <div className="mt-5 flex items-center justify-between">
              <div>
                <p className="text-[13px] font-semibold text-neutral-950">Quantity</p>
                <p className="mt-0.5 text-[12px] text-neutral-500">{formatCurrency(unitTotal)} each</p>
              </div>
              <div className="flex h-9 items-center rounded-xl border border-neutral-200 bg-white">
                <button
                  type="button"
                  onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                  disabled={quantity <= 1}
                  className="flex h-9 w-9 items-center justify-center text-neutral-600 disabled:opacity-30"
                  aria-label="Reduce quantity"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-8 text-center text-[14px] font-bold">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((current) => current + 1)}
                  className="flex h-9 w-9 items-center justify-center text-neutral-600"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </section>

          {optionGroups.map((group) => {
            const options = group.menu_options || [];
            return (
              <section key={group.id} className="border-b border-neutral-100 py-5">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-[17px] font-bold text-neutral-950">{group.name}</h2>
                    <p className="mt-1 text-[12px] text-neutral-500">
                      {group.is_required ? 'Choose one option' : 'Optional'}
                    </p>
                  </div>
                  {group.is_required && (
                    <span className="text-[11px] font-semibold text-neutral-400">Required</span>
                  )}
                </div>

                <div className="space-y-2">
                  {options.map((option) => {
                    const isSelected = selectedOptions[group.id] === option.id;
                    const priceModifier = Number(option.price_modifier || 0);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleOption(group.id, option.id)}
                        className="flex w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-3 text-left transition"
                        style={isSelected ? { borderColor: accentColor, backgroundColor: `${accentColor}08` } : { borderColor: '#e5e5e5', backgroundColor: '#fff' }}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
                            style={isSelected ? { backgroundColor: accentColor, borderColor: accentColor } : { borderColor: '#d4d4d4' }}
                          >
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </span>
                          <span className="truncate text-[14px] font-semibold text-neutral-800">{option.name}</span>
                        </div>
                        {priceModifier > 0 && (
                          <span className="shrink-0 text-[13px] font-semibold text-neutral-500">+{formatCurrency(priceModifier)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <section className="py-5">
            <label htmlFor="specialInstructions" className="text-[17px] font-bold text-neutral-950">Kitchen note</label>
            <textarea
              id="specialInstructions"
              placeholder="Example: no chilli, sauce on the side"
              value={specialInstructions}
              onChange={(event) => setSpecialInstructions(event.target.value)}
              rows={4}
              className="mt-3 w-full resize-none rounded-xl border border-neutral-200 bg-white px-3.5 py-3 text-[14px] leading-6 text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-400"
            />
          </section>
        </div>
      </main>

      <footer className="shrink-0 border-t border-neutral-200 bg-white px-5 py-3">
        <button
          type="button"
          onClick={handleAddToCart}
          className="mx-auto flex h-12 w-full max-w-2xl items-center justify-between rounded-xl px-4 text-[14px] font-bold text-white active:scale-[0.99]"
          style={{ backgroundColor: accentColor }}
        >
          <span>{isEditing ? 'Update cart' : 'Add to cart'}</span>
          <span>{formatCurrency(finalTotal)}</span>
        </button>
      </footer>
    </div>
  );
}
